import {
  effect,
  EnvironmentProviders,
  inject,
  makeEnvironmentProviders,
  provideAppInitializer,
  untracked,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs';
import { AuthStore } from '@domains/auth/auth.store';
import { RoleStore } from '@domains/role/role.store';
import { ProductFruitsService } from '@core/services/product-fruits.service';

// Starts once the user AND their rights are loaded (products/sports are derived
// from rights), and never on a logged-out page — an expired session is sent to
// /auth by the auth interceptor without clearing the store. ProductFruits
// follows SPA route changes on its own, so it is not re-initialised per route.
export function provideProductFruits(): EnvironmentProviders {
  return makeEnvironmentProviders([
    provideAppInitializer(() => {
      const router = inject(Router);
      const auth = inject(AuthStore);
      const roles = inject(RoleStore);
      const productFruits = inject(ProductFruitsService);

      const url = toSignal(
        router.events.pipe(
          filter((e) => e instanceof NavigationEnd),
          map((e) => e.urlAfterRedirects),
        ),
        { initialValue: '/auth' },
      );

      effect(() => {
        const user = auth.currentUser();
        const onAuthPage = url().startsWith('/auth');

        if (!user || onAuthPage) {
          untracked(() => productFruits.destroy());
        } else if (roles.currentUserPermissionsLoaded()) {
          const permissions = roles.currentUserPermissions();
          untracked(() => productFruits.init(user, permissions));
        }
      });
    }),
  ]);
}
